/**
 * 🚀 PERSISTENT CACHE SYSTEM - CURSOR V97.0
 * Provides persistent caching with localStorage backing for true instant loading
 * Maintains memory cache for fastest access with localStorage persistence
 * Handles localStorage limits gracefully with fallback to memory-only
 */

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number;
  version?: string;
}

interface CacheConfig {
  maxAge: number;
  staleWhileRevalidate?: number;
  version?: string;
}

class PersistentCache {
  private memoryCache = new Map<string, CacheEntry<any>>();
  private configs = new Map<string, CacheConfig>();
  private readonly CACHE_PREFIX = 'blissnet_cache_';
  private readonly MAX_STORAGE_SIZE = 4.5 * 1024 * 1024; // 4.5MB limit for safety

  constructor() {
    this.initializeFromStorage();
    this.setupCleanup();
  }

  /**
   * 🚀 Set data in both memory and localStorage
   */
  set<T>(key: string, data: T, ttl: number = 5 * 60 * 1000, version?: string): void {
    const entry: CacheEntry<T> = {
      data,
      timestamp: Date.now(),
      ttl,
      version
    };
    
    // Store in memory for fast access
    this.memoryCache.set(key, entry);
    
    // Store in localStorage for persistence
    try {
      const storageKey = this.CACHE_PREFIX + key;
      const serialized = JSON.stringify(entry);
      
      // Check storage size before writing
      if (this.getStorageSize() + serialized.length > this.MAX_STORAGE_SIZE) {
        console.warn('🚨 localStorage approaching limit, clearing old entries');
        this.cleanupOldestEntries();
      }
      
      localStorage.setItem(storageKey, serialized);
    } catch (error) {
      console.warn('🚨 localStorage write failed, using memory only:', error);
      // Continue with memory cache only
    }
  }

  /**
   * 🚀 Get data from memory first, then localStorage
   */
  get<T>(key: string): T | null {
    // Check memory first (fastest)
    let entry = this.memoryCache.get(key);
    
    // Fallback to localStorage if not in memory
    if (!entry) {
      try {
        const storageKey = this.CACHE_PREFIX + key;
        const stored = localStorage.getItem(storageKey);
        
        if (stored) {
          entry = JSON.parse(stored);
          // Restore to memory cache for future fast access
          this.memoryCache.set(key, entry);
        }
      } catch (error) {
        console.warn('🚨 localStorage read failed:', error);
        return null;
      }
    }
    
    if (!entry) {
      return null;
    }

    const now = Date.now();
    const age = now - entry.timestamp;
    
    // Check if data is fresh
    if (age < entry.ttl) {
      return entry.data;
    }
    
    // Check if data is stale but can be used while revalidating
    const config = this.configs.get(key);
    if (config?.staleWhileRevalidate && age < config.staleWhileRevalidate) {
      console.log(`🔄 Using stale cache for ${key} while revalidating`);
      return entry.data;
    }
    
    // Data is expired, remove it
    this.delete(key);
    return null;
  }

  /**
   * 🚀 Delete from both memory and localStorage
   */
  delete(key: string): boolean {
    const memoryDeleted = this.memoryCache.delete(key);
    
    try {
      const storageKey = this.CACHE_PREFIX + key;
      localStorage.removeItem(storageKey);
    } catch (error) {
      console.warn('🚨 localStorage delete failed:', error);
    }
    
    return memoryDeleted;
  }

  /**
   * 🚀 Clear all cached data
   */
  clear(): void {
    this.memoryCache.clear();
    
    try {
      // Clear all cache entries from localStorage
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(this.CACHE_PREFIX)) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.warn('🚨 localStorage clear failed:', error);
    }
  }

  /**
   * 🚀 Register cache configurations
   */
  registerCache(key: string, config: CacheConfig): void {
    this.configs.set(key, config);
  }

  /**
   * 🚀 Smart cache with background refresh
   */
  async getWithBackgroundRefresh<T>(
    key: string, 
    fetchFn: () => Promise<T>, 
    config?: CacheConfig
  ): Promise<T> {
    // Try cache first
    const cached = this.get<T>(key);
    if (cached) {
      // Background refresh if data is getting stale
      const entry = this.memoryCache.get(key);
      if (entry && config?.staleWhileRevalidate) {
        const age = Date.now() - entry.timestamp;
        if (age > config.maxAge && age < config.staleWhileRevalidate) {
          // Background refresh
          fetchFn().then(freshData => {
            this.set(key, freshData, config.maxAge, config.version);
          }).catch(console.error);
        }
      }
      return cached;
    }

    // No cache, fetch fresh data
    const freshData = await fetchFn();
    this.set(key, freshData, config?.maxAge || 5 * 60 * 1000, config?.version);
    return freshData;
  }

  /**
   * 🚀 Batch cache operations
   */
  setBatch(entries: Array<{ key: string; data: any; ttl?: number }>): void {
    entries.forEach(({ key, data, ttl }) => {
      this.set(key, data, ttl);
    });
  }

  /**
   * 🚀 Get cache statistics
   */
  getStats(): { 
    memorySize: number; 
    storageSize: number; 
    memoryKeys: string[];
    storageKeys: string[];
  } {
    const storageKeys = Object.keys(localStorage)
      .filter(key => key.startsWith(this.CACHE_PREFIX))
      .map(key => key.replace(this.CACHE_PREFIX, ''));
    
    return {
      memorySize: this.memoryCache.size,
      storageSize: this.getStorageSize(),
      memoryKeys: Array.from(this.memoryCache.keys()),
      storageKeys
    };
  }

  /**
   * 🚀 Smart cache cleanup
   */
  cleanup(): void {
    const now = Date.now();
    
    // Clean memory cache
    for (const [key, entry] of this.memoryCache.entries()) {
      if (now - entry.timestamp > entry.ttl) {
        this.memoryCache.delete(key);
      }
    }
    
    // Clean localStorage
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(this.CACHE_PREFIX)) {
          try {
            const stored = localStorage.getItem(key);
            if (stored) {
              const entry = JSON.parse(stored);
              if (now - entry.timestamp > entry.ttl) {
                localStorage.removeItem(key);
              }
            }
          } catch (error) {
            // Remove corrupted entries
            localStorage.removeItem(key);
          }
        }
      });
    } catch (error) {
      console.warn('🚨 localStorage cleanup failed:', error);
    }
  }

  /**
   * 🚀 Initialize cache from localStorage on startup
   */
  private initializeFromStorage(): void {
    try {
      const keys = Object.keys(localStorage);
      let loadedCount = 0;
      
      keys.forEach(key => {
        if (key.startsWith(this.CACHE_PREFIX)) {
          try {
            const stored = localStorage.getItem(key);
            if (stored) {
              const entry = JSON.parse(stored);
              const cacheKey = key.replace(this.CACHE_PREFIX, '');
              
              // Only load non-expired entries
              if (Date.now() - entry.timestamp < entry.ttl) {
                this.memoryCache.set(cacheKey, entry);
                loadedCount++;
              } else {
                // Remove expired entries
                localStorage.removeItem(key);
              }
            }
          } catch (error) {
            // Remove corrupted entries
            localStorage.removeItem(key);
          }
        }
      });
      
      if (process.env.NODE_ENV === 'development') {
        console.log(`🚀 PersistentCache: Loaded ${loadedCount} entries from localStorage`);
      }
    } catch (error) {
      console.warn('🚨 Failed to initialize from localStorage:', error);
    }
  }

  /**
   * 🚀 Get current localStorage size
   */
  private getStorageSize(): number {
    try {
      let size = 0;
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(this.CACHE_PREFIX)) {
          const value = localStorage.getItem(key);
          if (value) {
            size += key.length + value.length;
          }
        }
      });
      return size;
    } catch (error) {
      return 0;
    }
  }

  /**
   * 🚀 Clean up oldest entries when storage is full
   */
  private cleanupOldestEntries(): void {
    try {
      const entries: Array<{ key: string; timestamp: number }> = [];
      const keys = Object.keys(localStorage);
      
      keys.forEach(key => {
        if (key.startsWith(this.CACHE_PREFIX)) {
          try {
            const stored = localStorage.getItem(key);
            if (stored) {
              const entry = JSON.parse(stored);
              entries.push({
                key,
                timestamp: entry.timestamp
              });
            }
          } catch (error) {
            // Remove corrupted entries
            localStorage.removeItem(key);
          }
        }
      });
      
      // Sort by timestamp (oldest first)
      entries.sort((a, b) => a.timestamp - b.timestamp);
      
      // Remove oldest 20% of entries
      const toRemove = Math.ceil(entries.length * 0.2);
      entries.slice(0, toRemove).forEach(({ key }) => {
        localStorage.removeItem(key);
        const cacheKey = key.replace(this.CACHE_PREFIX, '');
        this.memoryCache.delete(cacheKey);
      });
      
      console.log(`🚀 PersistentCache: Cleaned up ${toRemove} oldest entries`);
    } catch (error) {
      console.warn('🚨 Failed to cleanup oldest entries:', error);
    }
  }

  /**
   * 🚀 Setup periodic cleanup
   */
  private setupCleanup(): void {
    // Cleanup every 5 minutes
    setInterval(() => {
      this.cleanup();
    }, 5 * 60 * 1000);
  }
}

// 🚀 Global persistent cache instance
export const persistentCache = new PersistentCache();

// Pre-configure common caches for better performance
persistentCache.registerCache('user_profile', {
  maxAge: 10 * 60 * 1000, // 10 minutes
  staleWhileRevalidate: 30 * 60 * 1000, // 30 minutes
  version: '1.0'
});

persistentCache.registerCache('network_connections', {
  maxAge: 5 * 60 * 1000, // 5 minutes
  staleWhileRevalidate: 15 * 60 * 1000, // 15 minutes
  version: '1.0'
});

persistentCache.registerCache('discover_profiles', {
  maxAge: 3 * 60 * 1000, // 3 minutes
  staleWhileRevalidate: 10 * 60 * 1000, // 10 minutes
  version: '1.0'
});

persistentCache.registerCache('chat_messages', {
  maxAge: 1 * 60 * 1000, // 1 minute
  staleWhileRevalidate: 2 * 60 * 1000, // 2 minutes
  version: '1.0'
});

// 🚀 Session cache with 24-hour persistence
persistentCache.registerCache('instant_page_data_session', {
  maxAge: 24 * 60 * 60 * 1000, // 24 hours
  staleWhileRevalidate: 24 * 60 * 60 * 1000, // 24 hours
  version: '1.0'
});

export default persistentCache; 